/**
 * ChannelSelect · 渠道多选下拉
 *
 * 与 ADChannel 协议一致：
 * - value `[]`             → 全选（默认；与后端"少传字段"约定对齐）
 * - value `[UNSELECT_ALL]` → 全不选
 * - value `[id, ...]`      → 部分选择
 *
 * 数据来源：
 * - channels / allChannelIds 由父组件 ChannelCompare 通过 useChannelMap 获取后下发
 *
 * 内部封装：
 * - UI 显示翻译（全选时填充所有 ids，全不选时清空）
 * - onChange 反向归一（用户全选 → []；用户全清 → [UNSELECT_ALL]）
 * - ChannelLabel itemRender（视觉与 ADChannel 一致）
 *
 * ⚠️ 提交时机：
 *   用户每次点选只更新内部 pendingValue，**仅在下拉关闭（onClose）时**才回调
 *   父级 onChange，避免多次切换渠道导致的多次接口请求 / 重渲染。
 *
 * 父组件使用：
 *   const { channels, allChannelIds } = useChannelMap();
 *   const [channelIds, setChannelIds] = useState<string[]>([]);  // 默认全选
 *   <ChannelSelect
 *     value={channelIds}
 *     onChange={setChannelIds}
 *     channels={channels}
 *     allChannelIds={allChannelIds}
 *   />
 */
import React, { useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash-es';
import ChannelLabel, { ChannelIconType } from '@op/components/ChannelLabel';
import useIntls from '@opm/hooks/useIntls';
import { MultipleSelect } from '@opm/components/FilterCommon/components/MultipleSelect';
import { TOptionsItem } from '@opm/components/FilterCommon/types';

/** 全不选 sentinel 值（与 ADChannel/ADCountryRegion 协议一致） */
export const UNSELECT_ALL = '-99999';

export interface IChannelSelectProps {
  /**
   * 渠道值（与 ADChannel 协议一致）
   * - `[]`              → 全选
   * - `[UNSELECT_ALL]`  → 全不选
   * - `[id, ...]`       → 部分选择
   */
  value: string[];
  /** 值变化回调（接收的也是协议化的 value） */
  onChange: (value: string[]) => void;
  /** 全部渠道列表（由父组件通过 useChannelMap 获取后下发） */
  channels: TOptionsItem[];
  /** 全部渠道 id 列表（由父组件通过 useChannelMap 获取后下发） */
  allChannelIds: string[];
  /** 自定义 className */
  className?: string;
}

export default function ChannelSelect({
  value,
  onChange,
  channels: options,
  allChannelIds,
  className,
}: IChannelSelectProps) {
  const { intl, isEn } = useIntls();



  /**
   * 内部"待提交"值：
   * - 用户每次点选只更新这里（不触发父级 setState / 接口请求）
   * - 下拉关闭（onClose）时统一 commit 到父级 onChange
   * - 父级外部 value 变化（filter 重置等）时反向同步
   */
  const [pendingValue, setPendingValue] = useState<string[]>(value);

  // 父级 value 外部变化（如 filter 重置）→ 同步到 pending
  useEffect(() => {
    setPendingValue(value);
  }, [value]);

  /**
   * 显示给 MultipleSelect 的 value（基于 pendingValue，让用户实时看到自己的点选）：
   * - `[]`（全选）→ 所有 ids（按钮显示「All」）
   * - `[UNSELECT_ALL]`（全不选）→ `[]`（按钮显示「None」）
   * - 部分选择 → 原值
   */
  const displayValue = useMemo(() => {
    if (pendingValue.length === 1 && pendingValue[0] === UNSELECT_ALL) return [];
    if (pendingValue.length === 0) return allChannelIds;
    return pendingValue;
  }, [pendingValue, allChannelIds]);

  /**
   * 单次点选：仅更新 pending，不 commit 到父级
   * 归一逻辑（与原 onChange 一致）：
   * - 全清 → `[UNSELECT_ALL]`
   * - 全选 → `[]`
   * - 部分 → 原值
   */
  const handleChange = (next: string[] = []) => {
    if (next.length === 0) {
      setPendingValue([UNSELECT_ALL]);
      return;
    }
    if (allChannelIds.length > 0 && next.length === allChannelIds.length) {
      setPendingValue([]);
      return;
    }
    setPendingValue(next);
  };

  /**
   * 下拉关闭（点击外部 / Esc）：把 pendingValue 一次性 commit 到父级
   * 跟 value 相同时跳过，避免触发无谓的父级 state 更新
   */
  const handleClose = () => {
    if (!isEqual(pendingValue, value)) {
      onChange(pendingValue);
    }
  };

  /**
   * 选中态渲染（按钮里）：参考 MemeChannel —— 仅显示 icon，无文字
   * 多选场景下文字会变得很挤，icon 已能识别渠道
   */
  const itemRender = (item: any) => (
    <ChannelLabel
      channel_name={item?.value as ChannelIconType}
      isMarketing
      showText
      replaceText={isEn ? item.text : item.textCn}
      iconStyle={{ width: 15, height: 15 }}
    />
  );

  /** 下拉列表渲染：icon + 名称（保持完整可读性） */
  const optionsItemRender = (item: any) => (
    <ChannelLabel
      channel_name={item?.value as ChannelIconType}
      isMarketing
      replaceText={isEn ? item.text : item.textCn}
      iconStyle={{ width: 15, height: 15 }}
    />
  );

  return (
    <MultipleSelect
      title={intl('Channel')}
      titleCn="渠道"
      valueKeys="channel_ids"
      options={options}
      value={displayValue}
      change={handleChange}
      // 未连接版本不依赖 redux，传空对象占位
      filterValue={{}}
      itemRender={itemRender}
      optionsItemRender={optionsItemRender}
      searchable
      listHeight={300}
      matchButtonWidth={false}
      filterClassName={className}
      hideComma
      showItemsThreshold={5}
      maxShow={2}
      boxClassName='dbom-channel-compare__channel-select'
      // 关闭下拉时一次性 commit 到父级（避免每次点选触发请求）
      onClose={handleClose}
    />
  );
}
